<template>
    <div>
        <div class="new-product">
            <div class="my-4">
                <div v-if="step === currentStep">
                    <form @submit.prevent.stop="readyToSave" novalidate @input="productAcivateUpdateFlag('schedule')">
                        <div v-if="isReadOnly" class="color-1 my-4 p-4">
                            This product is currently inactive. To make changes, please activate it first.
                        </div>

                        <div class="px-4">
                            <div class="product-schedule__top-options d-flex flex-wrap mb-5">
                                <div class="product-schedule__availability d-flex flex-grow-1">
                                    <div class="panel mt-0">
                                        <h5>Availability - How to manage your resources availability.</h5>
                                        <div class="schedule-type-holder d-flex mt-2" :class="{ 'schedule-type-holder--readonly': isReadOnly }">
                                            <button type="button" class="schedule-type-holder__btn btn tertiary" :disabled="isReadOnly" :class="{'schedule-type-holder__btn--selected': form.availabilityType == availabilityType.value, 'notAllowed': isReadOnly}" v-for="availabilityType in getAvailabilityTypes" :key="availabilityType.value || availabilityType.label" @click="setAvailabiltyType(availabilityType)">{{availabilityType.label}}</button>

                                            <div class="ml-2 product-schedule__availability-width" v-if="form.availabilityType == AvailabilityTypes.FIXED_LIMIT">
                                                <label class="w-100">Max. availability</label>
                                                <input :disabled="isReadOnly" type="number" style="width: 70px;" v-model.number="form.capacity" @input="$v.form.capacity.$touch, productAcivateUpdateFlag('scheduleAvailability'), markAllSchedulesEdited(), setAvailabiltyOnGroups(false)">

                                                <div v-if="$v.form.capacity.$invalid" class="messages color-6">
                                                    <span v-if="!$v.form.capacity.required">Please set max availability.</span>
                                                    <span v-if="!$v.form.capacity.customRequired"></span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="product-schedule__virtual-rooster d-flex">
                                    <!-- Virtual Rooster -->
                                    <div class="panel mt-0 d-flex flex-column flex-grow-1">
                                        <h5>Virtual Roster <span class="fs-smaller dark" v-if="disableVirtualRooster">(Only available with "Availability - Limited by resource")</span></h5>

                                        <div class="w-100 d-flex flex-row">
                                            <div class="d-flex mr-2 pt-1">
                                                <checkbox :disabled="isReadOnly || disableVirtualRooster" :class="{ 'notAllowed opacity-50': isReadOnly || disableVirtualRooster }" v-model="virtualRooster"></checkbox>
                                            </div>

                                            <div class="d-flex flex-column flex-grow-1">
                                                <div class="w-100" :class="{'opacity-50': disableVirtualRooster}">Do not allocate resources to this activity until the first booking is made. </div>
                                                <div class="w-100 dark">
                                                    This allows you to share resources between different products on a first-come, first-served basis.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-sm-12">
                                    <div class="d-flex justify-content-between mb-2">
                                        <label class="mb-2">Schedule - Specify dates and hours for your activities.</label>
                                        <div class="d-flex align-items-center">
                                            <i :class="['product-schedule__sort-icon','darkest','fs-large','mr-3', isOrderDesc ? 'fas fa-sort-amount-down' : 'fas fa-sort-amount-up']" @click="toggleOrderDirection"></i>
                                            <button class="btn tertiary" v-if="form.id" :disabled="isReadOnly || getIsDownloadingOldGroups" type="button" @click="togglePastGroups"> {{hidePastGroups? 'Show':'Hide'}} older schedules</button>
                                        </div>
                                    </div>

                                    <div>
                                        <template v-if="filteredGroups.length > 0">
                                            <div class="form-group product-schedule d-flex bg-lightest" v-for="(group, index) in filteredGroups" :key="group.id || index">
                                                <div class="row w-100 pt-1 pl-3 pr-0 pb-1">
                                                    <div class="col-sm-12 my-2 pr-0">
                                                        <div class="row align-items-center w-100">
                                                            <!-- Left: label + edited -->
                                                            <div class="col d-flex align-items-center">
                                                                <input type="text" class="bg-lightest product-schedule__label-input" v-model="group.label" placeholder="Schedule name" :disabled="!group.isEditing || group.isEditing == false">
                                                                <span v-if="hasGroupPerOrderMissingCategory(group) || groupMissingResources(group)" class="product-schedule__warning-dot"></span>
                                                                <span class="fs-small dark ml-2" v-if="group.isEdited && group.isEdited == true">edited</span>
                                                            </div>

                                                            <!-- Center: Dates -->
                                                            <div class="col d-flex align-items-center justify-content-center">
                                                                <span class="product-schedule__dates-label">Dates</span>
                                                                <input type="text" class="bg-lightest ml-2 text-center product-schedule__dates-input" :value="formatDateRange(group.dateFrom, group.dateTo)" disabled>
                                                            </div>

                                                            <!-- Right: actions -->
                                                            <div class="col d-flex align-items-center justify-content-end pr-0">
                                                                <span class="bold darker fs-small mx-3 pointer" v-if="!group.readOnly && group.id" @click="duplicateRow(group)">Duplicate</span>
                                                                <span class="bold darker fs-small mx-3 pointer" v-if="!group.readOnly" @click="deleteItem(index, 'groups', group)">Delete</span>
                                                                <div v-if="!isCreatingProduct && !group.readOnly">
                                                                    <span class="bold darker fs-small mx-3 pointer" v-if="!group.isEditing || group.isEditing == false" @click="editGroupToggle(group, false)">{{(group.isEditing && group.isEditing)? 'Editing':'Edit'}}</span>
                                                                    <button class="btn tertiary mx-3" type="button" v-if="group.isEditing && group.isEditing == true && group.id" @click="cancelEditGroup(group, index)">Cancel</button>
                                                                    <button class="btn primary mx-3" type="button" v-if="group.isEditing && group.isEditing == true" :disabled="!groupHasChanges(group) || !hasWeekDays || hasRepeatedCustomsPrices(group) || hasPriceGroupDecompositionError(group) || hasCustomPricesWithoutCategory(group) || hasGroupPerOrderMissingCategory(group) || rulePaxNotReady || groupMissingResources(group)" @click="editGroupToggle(group, true)">Confirm</button>
                                                                </div>
                                                                <i class="ico mx-0 fs-normal pointer" :class="{'ico-arrow-top':group.expanded, 'ico-arrow-bottom':!group.expanded}" @click="toggleGroupExpanded(group)"></i>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div v-if="group.expanded" class="col-md-6">
                                                        <div class="col-sm-12 panel shaded mt-0 mb-3">
                                                            <!-- GROUP DATES HERE  -->
                                                            <product-schedule-group-time-dates v-bind:group.sync="group" :form="form" @update="productAcivateUpdateFlag('schedule')" @hasWeekDays="setWeekDays($event)"/>
                                                        </div>
                                                    </div>

                                                    <div v-if="group.expanded" class="col-md-6 pb-3">
                                                        <div class="row">
                                                            <div class="col-sm-12">
                                                                <div class="panel shadded mt-0">
                                                                    <div class="d-flex pb-3 mb-3 border-bottom-light justify-content-between align-items-center pointer" @click="toggleExpand(group)">
                                                                        <span class="font-weight-600">PRICES <span v-if="hasGroupPerOrderMissingCategory(group)" class="product-schedule__warning-dot"></span></span>
                                                                        <i class="ico mx-0 fs-normal" :class="{'ico-arrow-top':group.pricesExpanded, 'ico-arrow-bottom':!group.pricesExpanded}"></i>
                                                                    </div>

                                                                    <div class="product-schedule__panel-holder" :class="{'product-schedule__panel-holder--closed': !group.pricesExpanded}">
                                                                        <pricing-manager class="panel mt-0" style="padding:1.5rem 0; background: #F3F5F6;"
                                                                            v-model="group.productGenderPrices"
                                                                            :disabled="!group.isEditing || group.isEditing == false"
                                                                            :group="group"
                                                                            :backuped-pricing-type="group && group.backupGroup && group.backupGroup.selectedPrincingType ? group.backupGroup.selectedPrincingType : null"
                                                                            :priceTypeConfig="{isDisable: hasRestrictions(group.id), type: 'product'}"
                                                                            @isReadyToSubmitPricingManager="setIsReadyToSubmitPricingManager($event)"
                                                                        ></pricing-manager>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div class="col-sm-12">
                                                                <ResourcesManager class="panel shaded" :disabled="!group.isEditing || group.isEditing == false" :readOnly="group.readOnly" :form="form" :group="group" />
                                                            </div>
                                                            
                                                            <div class="col-sm-12">
                                                                <RulesManager class="panel shaded" v-if="hasDynamicPricingRevenuePlugin" :disabled="!group.isEditing || group.isEditing == false" :readOnly="group.readOnly" :group="group" type="productGroupPriceGroups"/>
                                                            </div>

                                                            <div class="col-sm-12">
                                                                <MinMaxManager class="panel shaded" @isReadyToSubmit="validateRulePax($event)"  :disabled="!group.isEditing || group.isEditing == false" :readOnly="group.readOnly" :form="form" :group="group" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </template>

                                        <div v-if="filteredGroups.length == 0" class="d-flex flex-column">
                                            <span class="dark fs-large w-100 text-center">Looks like you have no active schedules, feel free to <b @click="handleAddGroup" :class="['pointer', { notAllowed: isReadOnly }]">add a new one</b>!</span>
                                            <span v-if="pastGroups.length > 0" class="dark fs-small w-100 text-center">You have {{pastGroups.length}} older schedule{{pastGroups.length > 1? 's':''}} that are hidden, <b @click="togglePastGroups" class="pointer">show all</b></span>
                                        </div>

                                        <button class="btn tertiary" type="button" @click="handleAddGroup" :disabled="isReadOnly" :class="{ notAllowed: isReadOnly }">
                                            <i class="ico-add fs-smallest add-circle color-1"></i> Add new schedule
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex'
    import { numeric } from 'vuelidate/lib/validators'
    
    import ProductScheduleGroupTimeDates from './components/ProductScheduleGroupTimeDates.vue'
    import extendedVSelect from '@/extends/vue-select'
    import Timepicker from '@/tools/Timepicker'
    import Datepicker from 'vuejs-datepicker';
    import ResourcesManager from './components/ResourcesManager'
    import MinMaxManager from './components/MinMaxManager'
    import RulesManager from './components/RulesManager'
    import PricingManager from './components/PricingManager'
    import WeekDayPicker from './components/WeekDayPicker'
    import DurationPicker from './components/DurationPicker'
    import DatePickerNew from '@/tools/DatePicker'
    import { AvailabilityTypes, ProductShareTypes } from '@/enums/productAvailabilityTypes'
    import { PricingTypes } from '@/enums/pricingType'
    import { isBlockedByResourceErrors } from '@/utils/resourcesValidation'

    // Mixins
    import { productMixin } from '@/mixins/product'
    import { isPerOrderAudienceValid } from '@/utils/pricingTypeHelpers'

    import { eventFormStateHelper } from '@/store/helpers/eventFormStateHelper'

    export default {
        mixins: [
            productMixin
        ],
        props: {
            currentStep: Number,
        },
        data() {
            return {
                rulePaxNotReady: false,
                step: 2,
                showCapacity: true,
                form: {
                    isPrivate: false,
                    schedules: [],
                },
                defaultDuration: ['02', '00'],
                defaultScheduleStartTime: ['10', '00'],
                hidePastGroups: true,
                loadedPastGroups: false,
                olderScheduleIds: [],
                isEditingPerGroup: false,
                pastGroups: [],
                isReadyToSubmitPricingManager: true,
                hasWeekDays: true,
                isOrderDesc: true,
                ProductShareTypes,
                AvailabilityTypes
            }
        },
        validations: {
            form: {
                capacity: {
                    numeric,
                    customRequired: function() {
                        return this.maxAvailabilityCheck()
                    }
                },
            }
        },
        watch: {
            stepNavigation(val) {
                this.$emit('stepNavigationChanged', val);
            },
            'form.groups': {
                deep: true,
                handler() {
                    this.$emit('stepNavigationChanged', this.stepNavigation);
                }
            },
            'form.productShareType'(val) {
                if (!this.form || !Array.isArray(this.form.groups) || !this.form.groups.length) return;
                
                const newShareType = Number(val) === ProductShareTypes.VIRTUALROSTER ? ProductShareTypes.VIRTUALROSTER : ProductShareTypes.NOSHARE;

                this.form.groups.forEach(group => {
                    if (group.shareType === ProductShareTypes.VIRTUALROSTER && group.shareType !== newShareType) {
                        group.shareType = newShareType;
                    }
                });
            }
        },
        mounted() {
            this.$emit('stepNavigationChanged', this.stepNavigation);
        },
        created() {
            this.form = structuredClone(this.getClonedItem);

            if (this.form.productShareType == null) {
                this.$set(this.form, 'productShareType', ProductShareTypes.NOSHARE);
            }

            if (this.getSelectedItem) {
                this.toggleCapacity();
                // Turns old product versions to the new version:
                if(!this.form.version || this.form.version == 0){
                    this.$set(this.form, 'version', 1)
                }

            } else {
                if (this.form.groups === undefined || this.form.groups.length == 0) {
                    this.addGroup();
                }
            }

            // If we dont have a availabilityType we will set it as 0
            if (!this.form.availabilityType) {
                this.$set(this.form, 'availabilityType', AvailabilityTypes.FIXED_LIMIT)
            }

            if (this.hasDynamicPricingRevenuePlugin) this.downloadRulesGroup();

            this.validateResources(this.form);
            this.setAvailabiltyOnGroups(true);
            // Ensure initial order reflects current sort direction
            if (Array.isArray(this.form.groups)) {
                this.form.groups = this.sortGroups(this.form.groups);
            }
        },
        computed: {
            ...mapGetters([
                'getSelectedItem',
                'getClonedItem',
                'account',
                'getAvailabilityTypes',
                'getIsDownloadingOldGroups',
                'getAudienceTypeList',
                'getVatTypes',
                'getHasDynamicPricingRevenuePlugin',
                'getProductSchedulesRestrictions'
                ]),
            ...eventFormStateHelper.mapGetters([
                'hasResourceErrors'
            ]),
            isReadOnly() {
                return this.form && !this.form.active;
            },
            hasDynamicPricingRevenuePlugin() {
                return this.getHasDynamicPricingRevenuePlugin;
            },
            disableVirtualRooster() {
                return this.form.availabilityType != AvailabilityTypes.LIMITED_BY_RESOURCES;
            },
            virtualRooster: {
                get() {
                    return this.form.productShareType == ProductShareTypes.VIRTUALROSTER;
                },
                set(val) {
                    this.form.productShareType = val ? ProductShareTypes.VIRTUALROSTER : ProductShareTypes.NOSHARE;
                }
            },
            isCreatingProduct() {
                return this.getSelectedItem == null;
            },
            activeGroups() {
                if (!Array.isArray(this.form.groups)) return [];
                const filtered = this.form.groups.filter(group => {
                    return moment().subtract(1, 'day').isBefore(group.dateTo) || group.id == null;
                });
                // Only sort if not editing any group
                return this.isEditingPerGroup  ? filtered : this.sortGroups(filtered);
            },
            allGroups() {
                if (!Array.isArray(this.form.groups)) return [];
                // Only sort if not editing any group
                return this.isEditingPerGroup  ? this.form.groups : this.sortGroups(this.form.groups);
            },
            filteredGroups() {
                if (this.hidePastGroups == true ){
                    return this.activeGroups;
                } else {
                    return this.allGroups;
                }
            },
            hasPricePerGroupErrors() {
                return this.form.groups.some(group => {
                    if (!group.passengerSettings || !Array.isArray(group.passengerSettings.configurations)) {
                        return false;
                    }

                    let maxLimit = Infinity;

                    if (group.availabilityType === AvailabilityTypes.FIXED_LIMIT) {
                        maxLimit = this.form.capacity || Infinity;
                    } else if (group.availabilityType === AvailabilityTypes.LIMITED_BY_RESOURCES && Array.isArray(group.resources)) {
                        maxLimit = group.resources.reduce((acc, r) => acc + (r.maxSeats || 0), 0);
                    }

                    return group.passengerSettings.configurations.some(config =>
                        config.lowerBound > maxLimit || config.upperBound > maxLimit
                    );
                });
            }
            ,
            isScheduleActionButtonsDisabled() {
                const hasGlobalPricingIssues = this.filteredGroups.some(group =>
                    this.hasRepeatedCustomsPrices(group) ||
                    this.hasPriceGroupDecompositionError(group) ||
                    this.hasCustomPricesWithoutCategory(group)
                );

                return (
                    this.$v.form.$invalid ||
                    this.isEditingPerGroup ||
                    this.isBlockedByResourceErrors ||
                    !this.isReadyToSubmitPricingManager ||
                    this.rulePaxNotReady ||
                    hasGlobalPricingIssues ||
                    this.hasRedDotInSchedule ||
                    this.hasGroupsWithoutResources
                );
            },
            isBlockedByResourceErrors() {
                return isBlockedByResourceErrors(this.form, this.hasResourceErrors);
            },
            hasGroupsWithoutResources() {
                return this.form.groups.some(group => !group.readOnly && this.groupMissingResources(group));
            },
            hasRedDotInSchedule() {
                return this.filteredGroups.some(group => this.hasGroupPerOrderMissingCategory(group));
            },
            stepNavigation() {
                return {
                    canProceed: !this.isEditingPerGroup,
                    canSave: !this.isScheduleActionButtonsDisabled && !this.isReadOnly,
                    canGoBack: !this.isEditingPerGroup,
                    hasError: this.isScheduleActionButtonsDisabled,
                };
            },
        },
        methods: {
            ...mapActions([
                'getOldGroups',
                'downloadRulesGroup'
            ]),
            ...eventFormStateHelper.mapActions([
                'setHasResourceErrors'
            ]),
            hasRestrictions(groupId) {
                const rule = this.getProductSchedulesRestrictions;
                if (!rule || !groupId) return false;

                // Minimal guards, scalable
                if (rule.property !== 'TicketType') return false;
                if (rule.restrictionType !== 'ReadOnly') return false;

                const ids = Array.isArray(rule.identifiers) ? rule.identifiers : [];
                const isEmpty = ids.length === 0;
                const isInverted = String(rule.restrictionMode).toLowerCase() === 'inverted';
                const isListed = ids.includes(groupId);

                if (isInverted) {
                    // Inverted: [] -> all; [ids] -> all except the listed ones
                    return isEmpty ? true : !isListed;
                } else {
                    // Normal: [] -> all; [ids] -> only the listed ones
                    return isEmpty ? true : isListed;
                }
            },
            hasCustomPricesWithoutCategory(group) {
                if (!group || !Array.isArray(group.productGenderPrices) || !group.productGenderPrices) return false;

                return group.productGenderPrices.some(gp => {
                    const audienceType = Number(gp.audienceType);
                    return audienceType >= 500 && !gp.audienceTypeAssossiationHelper && !gp.audienceTypeAssossiation;
                });
            },
            hasPriceGroupDecompositionError(group) {
                if (!group || !group.passengerSettings || !group.passengerSettings.configurations || !group.productGenderPrices) {
                    return false;
                }

                for (const groupPrice of group.passengerSettings.configurations) {
                    for (const priceConfig of group.productGenderPrices) {
                        const grossPrices = priceConfig.productGenderPriceComponents.map(service => {
                            if (!service.isRemainingValue && service.component.selectedPrice != null) {
                                return parseFloat(service.component.selectedPrice.grossprice);
                            }
                            return 0;
                        });

                        const sumGrossPrices = grossPrices.reduce((total, price) => total + price, 0);
                        const hasError = priceConfig.productGenderPriceComponents.some(priceComponent => (
                            priceComponent.isRemainingValue && (groupPrice.price - sumGrossPrices < 0.1)
                        ));

                        if (hasError) return true;
                    }
                }

                return false;
            },
            hasRepeatedCustomsPrices(group) {
                const descriptions = new Set();

                for (const item of group.productGenderPrices) {
                    // Create a unique key by combining group and description
                    const groupPart = item.audienceTypeGroup ? item.audienceTypeGroup : 'NULL_GROUP';
                    const key = `${groupPart}||${item.audienceTypeDescription}`;

                    if (descriptions.has(key)) {
                        // Duplication found
                        return true;
                    }

                    descriptions.add(key);
                }

                // No duplication found
                return false;
            },
            validateResources(form) {
                const resourcesAreValid = !form.groups.some((group) => group.resources.some((resource) => resource.maxSeats > resource.availability));
                this.setHasResourceErrors(!resourcesAreValid);
            },
            groupMissingResources(group) {
                return this.form.availabilityType === AvailabilityTypes.LIMITED_BY_RESOURCES && (!Array.isArray(group.resources) || group.resources.length === 0);
            },
            setWeekDays(event) {
                this.hasWeekDays = event;
            },
            validateRulePax(e) {
                this.rulePaxNotReady = e;
            },
            taxIdToName(taxId) {
                if (this.getVatTypes) {
                    return this.getVatTypes.filter((tax) => {
                        return tax.id == taxId;
                    })[0].description;
                }
            },
            groupHasValidPerOrderAudience(group) {
                if (!group || !Array.isArray(group.productGenderPrices) || group.productGenderPrices.length === 0) return true;
                const selectedPricingType = { id: group.productGenderPrices[0].genderPriceType };
                return isPerOrderAudienceValid(selectedPricingType, group.productGenderPrices);
            },
            hasGroupPerOrderMissingCategory(group) {
                const pricingType = group.productGenderPrices[0];
                if (!pricingType || pricingType.genderPriceType !== PricingTypes.PerOrder) return false;

                // If per-order audience is invalid or there are custom prices without category, show the warning
                return !this.groupHasValidPerOrderAudience(group) || this.hasCustomPricesWithoutCategory(group);
            },
            setIsReadyToSubmitPricingManager() {
                this.isReadyToSubmitPricingManager = this.activeGroups.every(g => this.groupHasValidPerOrderAudience(g));
            },
            setIsReadyToSubmitResourcesManager(event) {
                const value = (this.isEditingPerGroup  && event !== true) ? false : event;
                this.setHasResourceErrors(!value);
            },
            togglePastGroups() {
                if (this.loadedPastGroups == false) {
                    // Download old groups
                    this.getOldGroups({
                        reservationId: this.form.id
                    }).then(response => {
                        this.$set(this.form, 'groups', this.form.groups.concat(response));
                        this.$emit('updateBackupGroup', response);
                        response.forEach(group => {
                            if (group.id && !this.olderScheduleIds.includes(group.id)) {
                                this.olderScheduleIds.push(group.id);
                            }
                        });
                        this.loadedPastGroups = true;
                    })
                }
                this.hidePastGroups = !this.hidePastGroups;
            },
            backupExistingGroups() {
                let groups = this.form.groups;
                if (groups.length > 0 && !groups[0].backupGroup) {
                    groups.map(group => {
                        group.backupGroup = structuredClone(group);
                    })
                }
            },
            backupSpecificGroup(group){
                let groupBackup = structuredClone(group);

                // Remove previous backup from backup
                if (groupBackup.backupGroup) {
                    delete groupBackup.backupGroup;
                }

                this.$set(group, 'backupGroup', groupBackup);
            },
            addGroup() {
                this.productAcivateUpdateFlag('schedule');
                
                if (this.form.groups === undefined) {
                    this.$set(this.form, 'groups', []);
                }

                this.form.groups.push(this.createEmptyGroupRow());
            },
            createEmptyGroupRow() {
                let emptyGroup = {
                    availabilityType: this.form.availabilityType,
                    capacity: this.form.capacity,
                    id: null,
                    expanded: true,
                    isEditing: true,
                    version: 1,
                    label: 'Group ' + (this.form.groups.length + 1),
                    description: null,
                    shareType: this.form.productShareType === ProductShareTypes.VIRTUALROSTER ? ProductShareTypes.VIRTUALROSTER : ProductShareTypes.NOSHARE,
                    tax: true, // Price includes taxes
                    dateFrom: this.nowDate(),
                    dateTo: this.nowDate(),
                    pricesExpanded: false,
                    resourcesExpanded:false,
                    rulesExpanded:false,
                    schedules: [this.empty_schedule()],
                    components: [],
                    productGenderPrices: [this.empty_price()],
                    productGroupPriceGroups: [],
                    resources: [],
                    duration: this.defaultDuration[0]+':'+this.defaultDuration[1]+":00",
                };

                emptyGroup.backupGroup = structuredClone(emptyGroup);

                return emptyGroup;
            },
            addRepetition(group) {
                if (!group.isEditing || group.isEditing == false) {
                    return false
                }

                let lastScheduleFromGroup = structuredClone(group.schedules[group.schedules.length - 1]);
                lastScheduleFromGroup.id = null;
                group.schedules.push(lastScheduleFromGroup);
            },
            removeSchedule(group, index, schedule) {
                if (!group.isEditing || group.isEditing == false && (schedule.id && schedule.id  !== null)) {
                    return false
                }

                if (group.schedules.length == 1) {
                    this.$dialog.alert('\
                        <div class="fs-larg font-weight-600 mb-4">Unable to delete this schedule.</div>\
                        <div>You need to have at least one repetition, if you need to delete this one please create another one before.</div>', 
                        { okText:'Ok' })
                    .then(() => {})
                    .catch(() => {})
                    
                    return false;
                }

                this.$dialog.confirm('\
                    <div class="fs-larg font-weight-600 mb-4">Delete schedule?</div>\
                    <div>If this schedule has any reservations, those will <span class="underlined bold color-6">be canceled!</span></div>')
                .then(() => {
                    group.schedules.splice(index, 1);
                    this.$set(group, 'schedules', group.schedules);
                })
                .catch(() => {});
            },
            empty_schedule() {
                return {
                    startTime: this.defaultScheduleStartTime[0]+':'+this.defaultScheduleStartTime[1]+":00",
                    repeatsAtWeekDays: [],
                    // Cutoff
                    cutOffAffectAllNewChannels: false,
                    cutOffChannels: [],
                    cutOffChannelsAgents: [],
                    cutOffChannelsMain: [],
                    cutOffTime: 1,
                    isCutoffEdited: false,
                    softCutOff: false
                }
            },
            empty_price() {
                return {
                    currency: "€",
                    value: 0,
                    vatType: 0,
                    genderPriceType: 1,
                    isTaxIncludedInPrice: true,
                    isExcludedFromBilling: false,
                    productGenderPriceComponents: [],
                }
            },
            deleteItem(row, deleteType, item) {
                // Deletes selected row - works for resources and schedules
                let vm = this;

                switch (deleteType) {
                    case 'groups':
                        this.$dialog.confirm('\
                            <div class="fs-larg font-weight-600 mb-4">Delete schedule group?</div>')
                    	.then(function () {
                            // Find if is an old or a new group
                            var isOldGroup = true;

                            if (typeof vm.pastGroups == "undefined" || vm.pastGroups == null || vm.pastGroups.length == 0) {
                                isOldGroup = false;
                            } else {
                                if (vm.pastGroups.find(pastGroup => {return pastGroup.id == item.id}) == undefined) {
                                    isOldGroup = false;
                                } else {
                                    isOldGroup = true;
                                }
                            }

                            let groupArray;
                            if (isOldGroup == true) {
                                groupArray = vm.pastGroups;
                            } else {
                                groupArray = vm.form.groups;
                            }

                            let index = groupArray.indexOf(item);
                            vm.$delete(groupArray, index);
                            vm.setIsReadyToSubmitPricingManager(true);
                            vm.productAcivateUpdateFlag('schedule');
                    	})
                    	.catch(() => {});
                        break;
                    default:
                        break;
                }
            },
            duplicateRow(row) {
                this.productAcivateUpdateFlag('schedule');
                let copiedRow = structuredClone(row);

                // Adds 'Copy string to group label'
                if (!copiedRow.label.startsWith('(Copy)')) {
                    copiedRow.label = '(Copy) ' + copiedRow.label;
                }

                if (copiedRow.id) {
                    copiedRow.id = null;
                }

                this.form.groups.push(
                    copiedRow
                )
            },
            editGroupToggle(group, isToSubmit = false){
                if (isToSubmit) {
                    const pricingReady = this.groupHasValidPerOrderAudience(group);
                    const pricePerGroupErrors = this.hasPricePerGroupErrors;
                    const decompositionError = this.hasPriceGroupDecompositionError(group);

                    if (!pricingReady || pricePerGroupErrors || decompositionError) {
                        this.$toasted.error('Please check the errors on the prices form manager tab before submitting.', {duration: 3000});
                        return false;
                    }
                }

                if (this.hasResourceErrors && isToSubmit && this.form.availabilityType === AvailabilityTypes.LIMITED_BY_RESOURCES) {
                    this.$toasted.error('Please check the errors on the resources manager tab before submitting.', {duration: 3000});
                    return false;
                }

                if (isToSubmit && this.groupMissingResources(group)) {
                    this.$toasted.error('Please add at least one resource before submitting.', {duration: 3000});
                    return false;
                }

                this.productAcivateUpdateFlag('schedule');
                this.productAcivateUpdateFlag('priceGroup');

                if (!group.isEditing) {
                    // Preserve current visible order before entering edit mode
                    try {
                        const currentOrdered = this.sortGroups(this.form.groups);
                        this.form.groups = currentOrdered;
                    } catch (e) {}
                    this.isEditingPerGroup = true;
                    this.$set(group, 'isEditing', true);
                    this.$set(group, 'expanded', true);
                    // Creates a copy of each gruop
                    this.backupExistingGroups();
                    if (!group.isEdited || group.isEdited == false) {
                        this.$nextTick(() => { this.backupSpecificGroup(group); });
                    }
                } else {
                    if (group.isEditing == false) {
                        this.$set(group, 'isEditing', true);
                        this.$set(group, 'expanded', false);
                        this.backupSpecificGroup(group);
                    } else {
                        // If submitting, sort after submit
                        if (isToSubmit) {
                            this.form.groups = this.sortGroups(this.form.groups);
                        }
                        // Check for changes on weekdays - if there were removed days we will show a warning, if the user
                        // Presses ok -> everything will proceed, if press Cancel -> Weekdays revert back and submit is canceled
                        let showWeekdaysWarning = false;
                        for (let schedule of group.schedules) {
                            let backupSchedule = group.backupGroup.schedules.find(backupScheduleItem => backupScheduleItem.id == schedule.id);

                            if (backupSchedule) {
                                // RepeatsAtWeekDays
                                let removedWeekDays = backupSchedule.repeatsAtWeekDays.filter(backupWkDay => {
                                    return schedule.repeatsAtWeekDays.filter(currentWkDay => {return currentWkDay == backupWkDay}).length == 0;
                                })

                                if (removedWeekDays.length > 0) {
                                    showWeekdaysWarning = true;
                                }
                            }
                        }
                        if (this.rulePaxNotReady) {
                            this.$toasted.error('Check all fields on rules PAX');

                        } else if (showWeekdaysWarning == true) {
                            this.$dialog.confirm('One or more days of the week were removed.<br>Some events might be lost, proceed?', {
                                okText: 'Yes', cancelText: 'No'
                            }).then(() => {
                                this.checkScheduleChanges(group);
                                this.markGroupAsSubmitted(group);
                            }).catch(() => {
                                // Resets schedules
                                group.schedules = structuredClone(group.backupGroup.schedules);
                            });
                        } else {
                            this.checkScheduleChanges(group);
                            this.markGroupAsSubmitted(group);
                        }
                    }
                }
            },
            markGroupAsSubmitted(group) {
                this.$set(group, 'isEditing', false);
                this.$set(group, 'isEdited', true);
                this.$set(group, 'expanded', false);
                this.isEditingPerGroup = false;
                this.olderScheduleIds = this.olderScheduleIds.filter(id => id !== group.id);
            },
            tagProductToShowApplyAllEvents(group, reason) {
                if (group.id == null) {
                    return false;
                }

                this.$set(this.form, 'showApplyAllEventQuestion', true);
            },
            validatePricePerGroup(group, genderPriceChangesArray) {
                const passengerSettings = group.passengerSettings;
                const backupSettings = group.backupGroup && group.backupGroup.passengerSettings;

                if (!passengerSettings || !backupSettings) return;

                const hasConfigs = passengerSettings.configurations && backupSettings.configurations;
                const hasTiered = passengerSettings.tieredConfigurations && backupSettings.tieredConfigurations;

                if (!hasConfigs && !hasTiered) return;

                const formatsLabel = {
                    0: 'Price per Group',
                    1: 'Price per Person',
                    2: 'Price per Category Tier'
                };

                const formatConfig = ({ lowerBound, upperBound, vat, price }) => `${lowerBound}${upperBound == null ? '+' : ` - ${upperBound}`} (Pax) | ` + `${vat}% (Tax) | ${price}€`;

                const emitChange = (label, oldVal, newVal, showEvents = true ) => {
                    this.tagProductToShowApplyAllEvents(group, 'Price group');
                    
                    this.pushChangeToArray(
                        genderPriceChangesArray,
                        label,
                        'Prices',
                        oldVal,
                        newVal,
                        oldVal,
                        newVal,
                        showEvents
                    );
                };

                // ────────────────── Format validation ───────────────
                const { format: newFormat } = passengerSettings;
                const { format: oldFormat } = backupSettings;

                if (newFormat !== oldFormat) {
                    emitChange('Format', formatsLabel[oldFormat], formatsLabel[newFormat]);
                }

                // ─────────────────── Per Group && Per Person ───────────
                if (newFormat !== 2 && hasConfigs) {
                    passengerSettings.configurations.forEach((cfg, i) => {
                        const backupCfg = backupSettings.configurations[i];
                        
                        if (!backupCfg) return;

                        if (JSON.stringify(cfg) !== JSON.stringify(backupCfg)) {
                            emitChange('Group', formatConfig(backupCfg), formatConfig(cfg));
                        }
                    });

                    return;
                }

                // ───────────────── Per Category Tier ─────────────
                if (newFormat === 2 && hasTiered) {
                    Object.entries(passengerSettings.tieredConfigurations).forEach(([key, backupTier]) => {
                        const newPrice = group.productGenderPrices.find(gp => gp.hashedAudience === key);
                        
                        if (!newPrice) return;

                        const newTier = newPrice.tieredConfigurations;
                        const audienceDesc = newPrice.audienceTypeDescription;

                        backupTier.forEach((oldCfg, i) => {
                            const newCfg = newTier[i];
                            
                            if (!newCfg) return;

                            if (JSON.stringify(oldCfg) !== JSON.stringify(newCfg)) {
                                emitChange( `Group ${audienceDesc}`, formatConfig(oldCfg), formatConfig(newCfg));
                            }
                        });
                    }
                    );
                }
            },
            validateGenderPriceDependencies(group) {
                const current = group.productGenderPrices || [];
                const backup = (group.backupGroup && group.backupGroup.productGenderPrices) || [];

                const reducePrices = prices => {
                    return prices.reduce((acc, p) => {
                        const id = p.id != null ? p.id : p.hashedAudience;
                        const audienceType = p.audienceType;
                        const label = (p.audienceTypeHelper && p.audienceTypeHelper.description) || p.audienceTypeDescription || '';
                        const rawDeps = Array.isArray(p.genderPriceDependencies) ? p.genderPriceDependencies : [];
                        
                        const deps = rawDeps.map(gpd => {
                            const parent = gpd.genderPriceDependsOn || {};
                            
                            return {
                                parentId: parent.id,
                                audienceType: parent.audienceType,
                                audienceTypeDescription: parent.audienceTypeDescription,
                                audienceTypeGroup: parent.audienceTypeGroup,
                                isDeleted: gpd.isDeleted,
                                quantity: gpd.quantity
                            };
                        })
                        .sort((a, b) => {
                            if (a.parentId !== b.parentId) return a.parentId - b.parentId;
                            
                            return (a.audienceType || 0) - (b.audienceType || 0);
                        });

                        const dependencySettings = p.dependencySettings || null;

                        acc.push({
                            id,
                            audienceType,
                            label,
                            dependencies: deps,
                            dependencySettings
                        });

                        return acc;
                    }, []);
                };

                const currentPrices = reducePrices(current);
                const backupPrices = reducePrices(backup);

                return {
                    hasChanges: JSON.stringify(currentPrices) !== JSON.stringify(backupPrices),
                    currentPrices,
                    backupPrices,
                }
            },
            checkScheduleChanges(group) {
                // ----------------- Root group changes ---------------------
                let rootEditedFields = group.editedFields == undefined ? [] : group.editedFields;
                let genderPriceEditedFields = group.editedFieldsGenderPrice == undefined ? [] : group.editedFieldsGenderPrice;
                let resourcesEditedFields = [];
                let rulesPaxChangesArray = [];
                let shcedulesEditedFields = group.editedFieldsSchedules == undefined ? [] : group.editedFieldsSchedules;

                const hasNewScheduleWithRules = (group) => {
                    return group.productGroupPriceGroups.some(group => !group.isInGroup);
                }

                if (!_.isEqual(group.productGroupPriceGroups, group.backupGroup.productGroupPriceGroups) || hasNewScheduleWithRules(group)) {
                    this.tagProductToShowApplyAllEvents(group,'Rules');
                    
                    if (group.editedFieldsPriceGroupsAdded !== undefined && group.editedFieldsPriceGroupsAdded.length > 0) group.editedFieldsPriceGroupsAdded = [];
                    if (group.editedFieldsPriceGroupsRemoved !== undefined && group.editedFieldsPriceGroupsRemoved.length > 0) group.editedFieldsPriceGroupsRemoved = [];

                    group.productGroupPriceGroups.map(ruleGroup => {
                        if (!ruleGroup.isInGroup) {
                            this.pushChangeToArrayAddedItem(
                                group,
                                'editedFieldsPriceGroupsAdded',
                                ruleGroup.priceGroup.name,
                                ''
                            );
                        } else if (ruleGroup.isDeleted) {
                            this.pushChangeToArrayRemovedItem(
                                group,
                                'editedFieldsPriceGroupsRemoved',
                                ruleGroup.priceGroup.name,
                                ''
                            );
                        }
                    })
                }

                // Label
                if (group.label !== group.backupGroup.label) {
                    this.tagProductToShowApplyAllEvents(group,'Label');
                    this.pushChangeToArray(
                        rootEditedFields,
                        'Label',
                        'label',
                        group.backupGroup.label,
                        group.label,
                        group.backupGroup.label,
                        group.label,
                    );
                }

                // Duration
                if (group.duration !== group.backupGroup.duration) {
                    this.tagProductToShowApplyAllEvents(group,'Duration');
                    this.pushChangeToArray(
                        rootEditedFields,
                        'Duration',
                        'duration',
                        group.backupGroup.duration.slice(0, 5),
                        group.duration.slice(0, 5),
                        group.backupGroup.duration,
                        group.duration,
                    );
                }

                // IsTaxIncludedInPrice
                if (group.productGenderPrices[0] && group.productGenderPrices[0].isTaxIncludedInPrice != group.backupGroup.productGenderPrices[0].isTaxIncludedInPrice) {
                    this.tagProductToShowApplyAllEvents(group,'Tax excluded from price');
                    this.pushChangeToArray(
                        rootEditedFields,
                        'Tax excluded from price',
                        'isTaxIncludedInPrice',
                        group.backupGroup.productGenderPrices[0].isTaxIncludedInPrice?'Yes':'No',
                        group.productGenderPrices[0].isTaxIncludedInPrice?'Yes':'No',
                        group.backupGroup.productGenderPrices[0].isTaxIncludedInPrice,
                        group.productGenderPrices[0].isTaxIncludedInPrice,
                    );
                }

                // GenderPriceType
                if (group.productGenderPrices[0] && group.productGenderPrices[0].genderPriceType != group.backupGroup.productGenderPrices[0].genderPriceType) {
                    this.tagProductToShowApplyAllEvents(group,'Ticket type');
                    this.pushChangeToArray(
                        rootEditedFields,
                        'Ticket type',
                        'productGenderPrices',
                        group.backupGroup.productGenderPrices[0].genderPriceType == 1 ?'Per Order':'Per Person',
                        group.productGenderPrices[0].genderPriceType == 1 ?'Per Order':'Per Person',
                        group.backupGroup.productGenderPrices[0].productGenderPrices,
                        group.productGenderPrices[0].productGenderPrices,
                    );
                }

                // DateFrom
                if (group.dateFrom != group.backupGroup.dateFrom) {
                    this.tagProductToShowApplyAllEvents(group,'Starting date')
                    this.pushChangeToArray(
                        rootEditedFields,
                        'Starting date',
                        'dateFrom',
                        group.backupGroup.dateFrom,
                        group.dateFrom,
                        group.backupGroup.dateFrom,
                        group.dateFrom,
                    );
                }

                // DateTo
                if (group.dateTo != group.backupGroup.dateTo) {
                    this.tagProductToShowApplyAllEvents(group,'Ending date')
                    this.pushChangeToArray(
                        rootEditedFields,
                        'Ending date',
                        'dateTo',
                        group.backupGroup.dateTo,
                        group.dateTo,
                        group.backupGroup.dateTo,
                        group.dateTo,
                    );
                }

                // ShareType
                if (group.shareType != group.backupGroup.shareType) {
                    this.tagProductToShowApplyAllEvents(group,'Resource sharing')
                    this.pushChangeToArray(
                        rootEditedFields,
                        'Resource sharing',
                        'shareType',
                        group.backupGroup.shareType == ProductShareTypes.NOSHARE ? 'No' : 'Yes',
                        group.shareType == ProductShareTypes.NOSHARE ? 'No' : 'Yes',
                        group.backupGroup.shareType,
                        group.shareType,
                    );
                }

                this.$set(group, 'editedFields', rootEditedFields);

                let genderPriceChangesArray = [];

                 // ────────────────────── Per Group Validations (Per Person | Per Group | Per Category Tier ) ──────────────────────
                this.validatePricePerGroup(group, genderPriceChangesArray);

                let idx = 0;
                // ----------------- ProductGenderPrices ---------------------
                for (let genderPrice of group.productGenderPrices) {
                    let backupGenderPrice = group.backupGroup.productGenderPrices.find(backupGenderPriceItem => backupGenderPriceItem.id == genderPrice.id);

                    // Exists a backupGenderPrice
                    if (backupGenderPrice && (genderPrice.genderPriceType !== 3 || (genderPrice.genderPriceType === 3 && idx === 0))) {
                        // Gender Price Type
                        if (genderPrice.genderPriceType != backupGenderPrice.genderPriceType && genderPrice.genderPriceType !== 3) {
                            this.tagProductToShowApplyAllEvents(group,'Price');
                            this.pushChangeToArray(
                                genderPriceChangesArray,
                                'Price',
                                'genderPriceType',
                                backupGenderPrice.genderPriceType+'€',
                                genderPrice.genderPriceType+'€',
                                backupGenderPrice.genderPriceType,
                                genderPrice.genderPriceType,
                            );
                        }

                        // Label
                        if (genderPrice.audienceTypeLabel != backupGenderPrice.audienceTypeLabel && genderPrice.genderPriceType !== 3) {
                            this.tagProductToShowApplyAllEvents(group,'Label');
                            this.pushChangeToArray(
                                genderPriceChangesArray,
                                'Label',
                                'audienceTypeLabel',
                                backupGenderPrice.audienceTypeLabel,
                                genderPrice.audienceTypeLabel,
                                backupGenderPrice.audienceTypeLabel,
                                genderPrice.audienceTypeLabel,
                            );
                        }

                        // AudienceTypeOccupancy
                        if (genderPrice.audienceTypeOccupancy != backupGenderPrice.audienceTypeOccupancy && genderPrice.genderPriceType !== 3) {
                            this.tagProductToShowApplyAllEvents(group,'Occupancy');
                            this.pushChangeToArray(
                                genderPriceChangesArray,
                                'Occupancy',
                                'audienceTypeOccupancy',
                                backupGenderPrice.audienceTypeDescription,
                                genderPrice.audienceTypeDescription,
                                backupGenderPrice.audienceTypeOccupancy,
                                genderPrice.audienceTypeOccupancy,
                            );
                        }

                        // VatType
                        if (genderPrice.vatType != backupGenderPrice.vatType && genderPrice.genderPriceType !== 3) {
                            this.tagProductToShowApplyAllEvents(group,'Vat type');
                            this.pushChangeToArray(
                                genderPriceChangesArray,
                                'Vat type',
                                'vatType',
                                backupGenderPrice.vatType+'%',
                                genderPrice.vatType+'%',
                                backupGenderPrice.vatType,
                                genderPrice.vatType,
                            );
                        }

                        // GenderPriceType
                        if (genderPrice.audienceType != backupGenderPrice.audienceType) {
                            this.tagProductToShowApplyAllEvents(group,'Audience');
                            this.pushChangeToArray(
                                genderPriceChangesArray,
                                'Audience',
                                'audienceType',
                                backupGenderPrice.audienceTypeDescription,
                                genderPrice.audienceTypeDescription,
                                backupGenderPrice.audienceType,
                                genderPrice.audienceType,
                            );
                        }

                        // Value
                        if (genderPrice.value != backupGenderPrice.value && genderPrice.genderPriceType !== 3) {
                            this.tagProductToShowApplyAllEvents(group,'Price');
                            this.pushChangeToArray(
                                genderPriceChangesArray,
                                'Price',
                                'value',
                                backupGenderPrice.value+'€',
                                genderPrice.value+'€',
                                backupGenderPrice.value,
                                genderPrice.value,
                            );
                        }

                        // Iterate bkp components and compare them to the "currentPriceComponent" (the one still present on the price component array)
                        var thisPriceComponentIsChanged = false;
                        
                        for (var bkpPriceComponent of backupGenderPrice.productGenderPriceComponents) {
                            // Find matching existing price component (if any)
                            var currentPriceComponent = genderPrice.productGenderPriceComponents.find(priceComponent => {return priceComponent.component.id === bkpPriceComponent.component.id});

                            if (currentPriceComponent) {
                                // This component was not deleted - Check selected component (ie Lunch)
                                if (bkpPriceComponent.component.id !== currentPriceComponent.component.id) {
                                    thisPriceComponentIsChanged = true;
                                    this.tagProductToShowApplyAllEvents(group,'Price components');
                                    this.pushChangeToArray(
                                        genderPriceChangesArray,
                                        'Service',
                                        'productGenderPriceComponents',
                                        bkpPriceComponent.component.name,
                                        currentPriceComponent.component.name,
                                        bkpPriceComponent.component.id,
                                        currentPriceComponent.component.id,
                                    );
                                }
                                if (bkpPriceComponent.selectedPrice !== null && currentPriceComponent.selectedPrice !== null) {
                                    // Check price component selected option
                                    if (bkpPriceComponent.selectedPrice.id !== currentPriceComponent.selectedPrice.id) {
                                        thisPriceComponentIsChanged = true;
                                        this.tagProductToShowApplyAllEvents(group,'Price components');
                                        this.pushChangeToArray(
                                            genderPriceChangesArray,
                                            'Price option',
                                            'productGenderPriceComponents',
                                            bkpPriceComponent.selectedPrice.name,
                                            currentPriceComponent.selectedPrice.name,
                                            bkpPriceComponent.selectedPrice.id,
                                            currentPriceComponent.selectedPrice.id,
                                        );
                                    }
                                } else {
                                    if (bkpPriceComponent.component.id !== currentPriceComponent.component.id) {
                                        // If customRegularType changes or if value of component changes when it's type percent or open
                                        thisPriceComponentIsChanged = true;
                                        this.tagProductToShowApplyAllEvents(group,'Price components');
                                        this.pushChangeToArray(
                                            genderPriceChangesArray,
                                            'Price components',
                                            'productGenderPriceComponents',
                                            bkpPriceComponent.selectedPrice != null ?  bkpPriceComponent.selectedPrice.name : bkpPriceComponent.component.name ,
                                            currentPriceComponent.selectedPrice != null ?  currentPriceComponent.selectedPrice.name : currentPriceComponent.component.name,
                                            bkpPriceComponent.selectedPrice != null ? bkpPriceComponent.selectedPrice.id : bkpPriceComponent.component.id,
                                            currentPriceComponent.selectedPrice != null ? currentPriceComponent.selectedPrice.id : currentPriceComponent.component.id,
                                        );
                                    }
                                }

                            } else {
                                // Current price component not found, it must have been deleted
                                thisPriceComponentIsChanged = true;
                                this.tagProductToShowApplyAllEvents(group,'Price components');
                                this.pushChangeToArray(
                                    genderPriceChangesArray,
                                    'Removed ',
                                    'productGenderPriceComponentRemoved',
                                    null,
                                    bkpPriceComponent.component.name,
                                    null,
                                    bkpPriceComponent.component.name,
                                );
                            }
                        }

                        // Iterate existing price componente to find added price component (if the price component does not exist on bkp)
                        for (var existingPriceComponent of genderPrice.productGenderPriceComponents) {
                            var foundRemovedComponent = backupGenderPrice.productGenderPriceComponents.find(backupPriceComponent => {return backupPriceComponent.component.id == existingPriceComponent.component.id});
                            if (!foundRemovedComponent) {
                                thisPriceComponentIsChanged = true;
                                this.tagProductToShowApplyAllEvents(group,'Price components');
                                this.pushChangeToArray(
                                    genderPriceChangesArray,
                                    'Added ',
                                    'productGenderPriceComponentAdded',
                                    null,
                                    existingPriceComponent.component.name,
                                    null,
                                    existingPriceComponent.component.name,
                                    true
                                );
                            }
                        }

                        // If change is found add the entire price composition to resume popup
                        if (thisPriceComponentIsChanged == true) {
                            this.tagProductToShowApplyAllEvents(group,'Price components');
                            var itemNr = 1;
                            for (var currentPriceComponentAdd of genderPrice.productGenderPriceComponents) {
                                this.pushChangeToArray(
                                    genderPriceChangesArray,
                                    'Price item ' + itemNr + '/' + genderPrice.productGenderPriceComponents.length,
                                    'productGenderCompleteResume',
                                    null,

                                    currentPriceComponentAdd.component.selectedPrice != null
                                    ? currentPriceComponentAdd.component.name + ' - ' + currentPriceComponentAdd.component.selectedPrice.name + ' - ' + this.taxIdToName(currentPriceComponentAdd.component.selectedPrice.vatType) + ' - ' + this.formatPrice(currentPriceComponentAdd.component.selectedPrice.grossprice) + '€'
                                    : currentPriceComponentAdd.component.name,
                                        null,
                                        currentPriceComponentAdd.component.id,
                                        true
                                );

                                itemNr++;
                            }
                        }

                        // Search if this genderPrice already has changes object
                        let foundGenderPriceChangesObj = group.editedFieldsGenderPrice != undefined ? group.editedFieldsGenderPrice.find(i => i.genderPriceId == genderPrice.id) : false;
                        if (foundGenderPriceChangesObj) {
                            let index = group.editedFieldsGenderPrice.indexOf(foundGenderPriceChangesObj);

                            // Only overwrite existing entry if we actually have computed changes; otherwise keep previous non-empty changes
                            if (genderPriceChangesArray.length > 0) {
                                group.editedFieldsGenderPrice[index] = {
                                    genderPriceLabel: genderPrice.genderPriceType === 3 ? 'Price per Group' : ((genderPrice.audienceTypeDescription != null ? genderPrice.audienceTypeDescription : '') + (genderPrice.audienceTypeLabel != null ? ' '+ genderPrice.audienceTypeLabel : '')),
                                    genderPriceId: genderPrice.id,
                                    changes: genderPriceChangesArray
                                }
                            }
                        } else {
                            // Only add if there are any changes, this prevents empty changes array
                            if (genderPriceChangesArray.length > 0) {
                                genderPriceEditedFields.push({
                                    genderPriceLabel: genderPrice.genderPriceType === 3 ? 'Price per Group' : ((genderPrice.audienceTypeDescription != null ? genderPrice.audienceTypeDescription : '') + (genderPrice.audienceTypeLabel != null ? ' '+ genderPrice.audienceTypeLabel : '')),
                                    genderPriceId: genderPrice.id,
                                    changes:genderPriceChangesArray
                                });
                            }
                        }
                    }

                    idx++;
                }
                this.$set(group, 'editedFieldsGenderPrice', genderPriceEditedFields);


                let genderPriceServicesEditedFields = group.editedFieldsGenderPriceService == undefined ? [] : group.editedFieldsGenderPriceService;
                let genderPriceServiceChangesArray = [];

                // ----------------- Price Services ---------------------
                for (let genderPrice of group.productGenderPrices) {
                    let backupGenderPrice = group.backupGroup.productGenderPrices.find(backupGenderPriceItem => backupGenderPriceItem.id == genderPrice.id);
                
                    if (backupGenderPrice) {
                        for (var bkpPriceComponent of backupGenderPrice.productGenderPriceComponents) {
                            // Find matching existing price component (if any)
                            var currentPriceComponent = genderPrice.productGenderPriceComponents.find(priceComponent => {return priceComponent.component.id === bkpPriceComponent.component.id});

                            if (currentPriceComponent) {
                                if (bkpPriceComponent.component.id === currentPriceComponent.component.id && bkpPriceComponent.value !== currentPriceComponent.value || bkpPriceComponent.vatType !== currentPriceComponent.vatType) {
                                    this.tagProductToShowApplyAllEvents(group,'Price components');
                                    
                                    this.pushChangeToArray(
                                        genderPriceServiceChangesArray,
                                        'Service ' + currentPriceComponent.component.name + ' value',
                                        'productGenderPriceComponents',
                                        `${bkpPriceComponent.value}${bkpPriceComponent.valueType === 1 ? '%' : '€'} | ${this.taxIdToName(bkpPriceComponent.vatType)}`,
                                        `${currentPriceComponent.value}${currentPriceComponent.valueType === 1 ? '%' : '€'} | ${this.taxIdToName(currentPriceComponent.vatType)}`,
                                        `${bkpPriceComponent.value}${bkpPriceComponent.valueType === 1 ? '%' : '€'} | ${this.taxIdToName(bkpPriceComponent.vatType)}`,
                                        `${currentPriceComponent.value}${currentPriceComponent.valueType === 1 ? '%' : '€'} | ${this.taxIdToName(currentPriceComponent.vatType)}`,
                                        true
                                    );
                                }
                            }
                        }

                        // Search if this genderPrice already has changes object
                        let foundGenderPriceServicesChangesObj = group.editedFieldsGenderPriceServices != undefined ? group.editedFieldsGenderPriceServices.find(i => i.genderPriceId == genderPrice.id) : false;
                        
                        if (foundGenderPriceServicesChangesObj) {
                            let index = group.editedFieldsGenderPriceServices.indexOf(foundGenderPriceServicesChangesObj);

                            // Only overwrite existing entry if we actually have computed changes; otherwise keep previous non-empty changes
                            if (genderPriceServiceChangesArray.length > 0) {
                                group.editedFieldsGenderPriceServices[index] = {
                                    genderPriceLabel: genderPrice.genderPriceType === 3 ? 'Price per Group' : ((genderPrice.audienceTypeDescription != null ? genderPrice.audienceTypeDescription : '') + (genderPrice.audienceTypeLabel != null ? ' '+ genderPrice.audienceTypeLabel : '')),
                                    genderPriceId: genderPrice.id,
                                    changes: genderPriceServiceChangesArray
                                }
                            }
                        } else {
                            if (genderPriceServiceChangesArray.length > 0) {
                                genderPriceServicesEditedFields.push({
                                    genderPriceLabel: genderPrice.genderPriceType === 3 ? 'Price per Group' : ((genderPrice.audienceTypeDescription != null ? genderPrice.audienceTypeDescription : '') + (genderPrice.audienceTypeLabel != null ? ' '+ genderPrice.audienceTypeLabel : '')),
                                    genderPriceId: genderPrice.id,
                                    changes: genderPriceServiceChangesArray
                                });

                                genderPriceServiceChangesArray = [];
                            }
                        }
                    }
                }

                if (group.editedFieldsGenderPriceServices && group.editedFieldsGenderPriceServices.length > 0) {
                    genderPriceServicesEditedFields.forEach((editedService) => {
                        group.editedFieldsGenderPriceServices.push(editedService);
                    })
                } else {
                    this.$set(group, 'editedFieldsGenderPriceServices', genderPriceServicesEditedFields);
                }

                // ----------------- Resources ---------------------
                for (var i = 0; i < group.resources.length; i++) {
                    let resource = group.resources[i];
                    let resourcesChangesArray = [];
                    let backupResource = group.backupGroup.resources[i];

                    // Exists a backupResource
                    if (backupResource) {
                        // Max seats
                        if (resource.maxSeats != backupResource.maxSeats && resource.id == backupResource.id) {
                            this.tagProductToShowApplyAllEvents(group,'Max. Seats');
                            this.pushChangeToArray(
                                resourcesChangesArray,
                                '"'+resource.name + '" Max. Seats',
                                'maxSeats',
                                backupResource.maxSeats,
                                resource.maxSeats,
                                backupResource.maxSeats,
                                resource.maxSeats,
                            );
                        }
                        
                        resourcesEditedFields.push({
                            resourceId: resource.id,
                            changes:resourcesChangesArray
                        });
                    }
                }
                this.$set(group, 'editedFieldsResources', resourcesEditedFields);

                // ----------------- Rules (PAX) ---------------------

                // MinimumPaxPerBooking
                if (group.minimumPaxPerBooking !== group.backupGroup.minimumPaxPerBooking) {
                    this.tagProductToShowApplyAllEvents(group,'Rules Minimun Book');
                    this.pushChangeToArray(
                        rulesPaxChangesArray,
                        'Rules min booking',
                        'rulesMinBooking',
                        group.backupGroup.minimumPaxPerBooking,
                        group.minimumPaxPerBooking,
                        group.backupGroup.minimumPaxPerBooking,
                        group.minimumPaxPerBooking,
                    );
                }

                // MaximumPaxPerBooking
                if (group.maximumPaxPerBooking !== group.backupGroup.maximumPaxPerBooking) {
                    this.tagProductToShowApplyAllEvents(group,'Rules Maximum Book');
                    this.pushChangeToArray(
                        rulesPaxChangesArray,
                        'Rules max booking',
                        'rulesMaxBooking',
                        group.backupGroup.maximumPaxPerBooking,
                        group.maximumPaxPerBooking,
                        group.backupGroup.maximumPaxPerBooking,
                        group.maximumPaxPerBooking,
                    );
                }
                
                // MinimumLotation
                if (group.minimumLotation !== group.backupGroup.minimumLotation) {
                    this.tagProductToShowApplyAllEvents(group,'Rules minimum Lotation ');
                    this.pushChangeToArray(
                        rulesPaxChangesArray,
                        'Rules minimum lotation',
                        'rulesMinimumLotation',
                        group.backupGroup.minimumLotation,
                        group.minimumLotation,
                        group.backupGroup.minimumLotation,
                        group.minimumLotation,
                    );
                }
                
                // RejectBookingBasedOnMinMax
                if (group.rejectBookingBasedOnMinMax !== group.backupGroup.rejectBookingBasedOnMinMax) {
                    this.tagProductToShowApplyAllEvents(group,'Rules Reject Book');
                    this.pushChangeToArray(
                        rulesPaxChangesArray,
                        'Rules reject booking',
                        'rulesRejectBooking',
                        group.backupGroup.rejectBookingBasedOnMinMax,
                        group.rejectBookingBasedOnMinMax,
                        group.backupGroup.rejectBookingBasedOnMinMax,
                        group.rejectBookingBasedOnMinMax,
                    );
                }

                if (group.editedFields.length === 0) {
                    this.$set(group, 'editedFields', rulesPaxChangesArray);
                } else {
                    rulesPaxChangesArray.forEach((changes) => {
                        group.editedFields.push(changes)
                    })
                }

                // -------------------- Price Category Dependencies --------------------
                const genderPriceDependenciesValidations = this.validateGenderPriceDependencies(group);

                if (genderPriceDependenciesValidations.hasChanges) {
                    this.tagProductToShowApplyAllEvents(group, 'Price Dependencies');
                    const prices = genderPriceDependenciesValidations.currentPrices;

                    prices.forEach((p, idx) => {
                        const bakEntry = genderPriceDependenciesValidations.backupPrices[idx];
                        const bakDeps = bakEntry ? bakEntry.dependencies || [] : [];
                        const bakSettings = bakEntry ? bakEntry.dependencySettings : null;

                        p.dependencies.forEach((deps, depsIdx) => {
                            if (deps.parentId === 0) {
                                this.pushChangeToArrayPriceDependencies(
                                    group,
                                    'editedFieldsPriceDependencies',
                                    deps.audienceTypeDescription,
                                    'add',
                                    p.label
                                );
                            } else if (deps.isDeleted) {
                                this.pushChangeToArrayPriceDependencies(
                                    group,
                                    'editedFieldsPriceDependencies',
                                    deps.audienceTypeDescription,
                                    'remove',
                                    p.label
                                );

                                return;
                            }

                            if (!bakDeps[depsIdx]) return;

                            if (deps.quantity !== bakDeps[depsIdx].quantity) {
                                const newQuantity = deps.quantity;
                                const oldQuantity = bakDeps[depsIdx].quantity;
                                const depName = deps.audienceTypeDescription;
                                const final = {
                                    label: depName,
                                    newQuantity,
                                    oldQuantity
                                }

                                this.pushChangeToArrayPriceDependencies(
                                    group,
                                    'editedFieldsPriceDependencies',
                                    final,
                                    'quantity',
                                    p.label
                                );
                            }   
                        })

                        if (JSON.stringify(p.dependencySettings) !== JSON.stringify(bakSettings)) {
                            const newDependencySettings = p.dependencySettings;
                            const oldDependencySettings = bakSettings;
                            const final = {
                                new: newDependencySettings === null ? null : newDependencySettings.dependencyType === 0 ? 'AND' : 'OR',
                                old: oldDependencySettings === null ? null : oldDependencySettings.dependencyType === 0 ? 'AND' : 'OR'
                            }

                            this.pushChangeToArrayPriceDependencies(
                                group,
                                'editedFieldsPriceDependencies',
                                final,
                                'type',
                                p.label
                            );
                        }
                    })
                }

                // ----------------- Schedules ---------------------
                for (let schedule of group.schedules) {
                    let scheduleshangesArray = [];
                    let backupSchedule = group.backupGroup.schedules.find(backupScheduleItem => backupScheduleItem.id == schedule.id);

                    // Exists a backupResource
                    if (backupSchedule) {
                        // Repeats At Week Days
                        if (this.checkArrayDifference(schedule.repeatsAtWeekDays, backupSchedule.repeatsAtWeekDays).length > 0 || schedule.repeatsAtWeekDays.length != backupSchedule.repeatsAtWeekDays.length) {
                            let weekDays = [
                                { id: 1, active:false, name: 'Monday', nameSmall:'Mon',  value: 'M' },
                                { id: 2, active:false, name: 'Tuesday', nameSmall:'Tue',  value: 'T' },
                                { id: 3, active:false, name: 'Wednesday', nameSmall:'Wed',  value: 'W' },
                                { id: 4, active:false, name: 'Thursday', nameSmall:'Thu',  value: 'T' },
                                { id: 5, active:false, name: 'Friday', nameSmall:'Fri',  value: 'F' },
                                { id: 6, active:false, name: 'Saturday', nameSmall:'Sat',  value: 'S' },
                                { id: 0, active:false, name: 'Sunday', nameSmall:'Sun',  value: 'S' },
                            ]

                            let backupWeekDays = backupSchedule.repeatsAtWeekDays.map(w => {
                                return weekDays.find(weekday => weekday.id == w).value;
                            })
                            let newWeekDays = schedule.repeatsAtWeekDays.map(w => {
                                return weekDays.find(weekday => weekday.id == w).value;
                            })
                        }

                        // StartTime
                        if (schedule.startTime != backupSchedule.startTime) {
                            this.tagProductToShowApplyAllEvents(group,'Start hour');
                            this.pushChangeToArray(
                                scheduleshangesArray,
                                'Start hour',
                                'startTime',
                                backupSchedule.startTime.slice(0, 5),
                                schedule.startTime.slice(0, 5),
                                backupSchedule.startTime,
                                schedule.startTime,
                            );
                        }

                        // Cutoff - Enabled
                        if (schedule.hasCutOff != backupSchedule.hasCutOff) {
                            this.tagProductToShowApplyAllEvents(group,'Cutoff');
                            this.pushChangeToArray(
                                scheduleshangesArray,
                                'Cutoff',
                                'hasCutOff',
                                backupSchedule.hasCutOff && backupSchedule.hasCutOff == true? 'Enabled' : 'Disabled',
                                schedule.hasCutOff && schedule.hasCutOff == true? 'Enabled' : 'Disabled',
                                backupSchedule.hasCutOff,
                                schedule.hasCutOff,
                            );
                        }

                        // Cutoff - Hour Changed
                        if (schedule.cutOffTime != backupSchedule.cutOffTime) {
                            this.tagProductToShowApplyAllEvents(group,'Cutoff');
                            this.pushChangeToArray(
                                scheduleshangesArray,
                                'Cutoff Time',
                                'cutOffTime',
                                backupSchedule.cutOffTime,
                                schedule.cutOffTime,
                                backupSchedule.cutOffTime,
                                schedule.cutOffTime,
                            );
                        }

                        // Cutoff - added channels
                        // Count amout of added channels
                        let newChannelCounter = 0
                        for (let newChannel of schedule.cutOffChannelsMain) {
                            // Search for added channels
                            let foundOldChannel = backupSchedule.cutOffChannelsMain.find(oldChannel => newChannel.channelId == oldChannel.channelId);
                            if (!foundOldChannel) {
                                newChannelCounter++;
                            }
                        }

                        if (newChannelCounter > 0) {
                            this.tagProductToShowApplyAllEvents(group,'Cutoff');
                            this.pushChangeToArray(
                                scheduleshangesArray,
                                'Cutoff Channels added',
                                'cutoffChannelsAdd',
                                null,
                                newChannelCounter,
                                null,
                                newChannelCounter,
                            );
                        }

                        // Cutoff - removed channels
                        // Count amout of removed channels
                        let removedChannelsCounter = 0;
                        for (let oldChannel of backupSchedule.cutOffChannelsMain) {
                            // Search for added channels
                            let foundOldChannel = schedule.cutOffChannelsMain.find(newChannel => oldChannel.channelId == newChannel.channelId);
                            if (!foundOldChannel) {
                                removedChannelsCounter++;
                            }
                        }

                        if (removedChannelsCounter > 0) {
                            this.tagProductToShowApplyAllEvents(group,'Cutoff');
                            this.pushChangeToArray(
                                scheduleshangesArray,
                                'Cutoff Channels removed',
                                'cutoffChannelsRemoved',
                                null,
                                removedChannelsCounter,
                                null,
                                removedChannelsCounter,
                            );
                        }

                        // Cutoff - softCutOff
                        if (schedule.softCutOff != backupSchedule.softCutOff) {
                            this.tagProductToShowApplyAllEvents(group,'Cutoff');
                            this.pushChangeToArray(
                                scheduleshangesArray,
                                'Soft cutoff',
                                'softCutOff',
                                backupSchedule.softCutOf == true? 'Enabled' : 'Disabled',
                                schedule.softCutOff == true? 'Enabled' : 'Disabled',
                                backupSchedule.softCutOff,
                                schedule.softCutOff,
                            );
                        }

                        // Cutoff - specific agents
                        // If the "Agents" channel is Enabled and the number of selected agents change
                        let foundAgentsChannelEnabled = schedule.cutOffChannelsMain.find(channel => channel.channelType == 50);

                        if (foundAgentsChannelEnabled) {
                            // Count added agents
                            let newAgentCounter = 0;
                            for (let newAgent of schedule.cutOffChannelsAgents) {
                                // Search for added channels
                                let foundOldAgent = backupSchedule.cutOffChannelsAgents.find(oldAgent => newAgent.id == oldAgent.id);
                                if (!foundOldAgent) {
                                    newAgentCounter++;
                                }
                            }

                            if (newAgentCounter > 0) {
                                this.tagProductToShowApplyAllEvents(group,'Cutoff');
                                this.pushChangeToArray(
                                    scheduleshangesArray,
                                    'Cutoff Agents added',
                                    'cutoffAgentsAdd',
                                    null,
                                    newAgentCounter,
                                    null,
                                    newAgentCounter,
                                );
                            }

                            // Count removed agents
                            let removedAgentCounter = 0;
                            for (let oldAgent of backupSchedule.cutOffChannelsAgents) {
                                // Search for added channels
                                let foundOldAgent = schedule.cutOffChannelsAgents.find(newAgent => oldAgent.agentId == newAgent.agentId);
                                if (!foundOldAgent) {
                                    removedAgentCounter++;
                                }
                            }

                            if (removedAgentCounter > 0) {
                                this.tagProductToShowApplyAllEvents(group,'Cutoff');
                                this.pushChangeToArray(
                                    scheduleshangesArray,
                                    'Cutoff Agents removed',
                                    'cutoffAgentsRemoved',
                                    null,
                                    removedAgentCounter,
                                    null,
                                    removedAgentCounter,
                                );
                            }
                        }

                        // Search if this schedule already has changes object
                        let foundscheduleChangesObj = group.editedFieldsSchedules != undefined ? group.editedFieldsSchedules.find(i => i.scheduleId == schedule.id) : false;

                        if (foundscheduleChangesObj) {
                            let index = group.editedFieldsSchedules.indexOf(foundscheduleChangesObj);
                            group.editedFieldsSchedules[index] = {
                                scheduleId: schedule.id,
                                changes:scheduleshangesArray
                            }
                        } else {
                            shcedulesEditedFields.push({
                                scheduleId: schedule.id,
                                changes:scheduleshangesArray
                            });
                        }
                    }
                }
                this.$set(group, 'editedFieldsSchedules', shcedulesEditedFields);

                // ************** find removed and added gender types **************
                this.findRemovedAndAddedGenderPrices(group);
                // ************** find removed and added schedules **************
                this.findRemovedAndAddedGenderSchedules(group);
                // ************** find removed and added resources **************
                this.findRemovedAndAddedGenderResources(group);
            },
            pushChangeToArrayPriceDependencies(group, arrayName, info, type, label) {
                if (!group[arrayName]) this.$set(group, arrayName, []);

                let entry = group[arrayName].find(e => e.genderPriceLabel === label);
                
                if (!entry) {
                    entry = { genderPriceLabel: label, infoToShow: [] };
                    group[arrayName].push(entry);
                }

                entry.infoToShow.push({ type, info });
            },
            findRemovedAndAddedGenderPrices(group) {
                // Check if group has any backedup gender prices
                let groupHasBackedupGenderPrices = group.backupGroup && group.backupGroup.productGenderPrices && group.backupGroup.productGenderPrices.length > 0;

                // If it has we will check if any of them were removed (to add to "removedArray")
                if (groupHasBackedupGenderPrices) {
                    for (let bkpGenderPrice of group.backupGroup.productGenderPrices) {
                        let foundInNormalArray = group.productGenderPrices.find(existingGenderPrice => {return existingGenderPrice.id == bkpGenderPrice.id});
                        if (foundInNormalArray == undefined) {
                            this.tagProductToShowApplyAllEvents(group,'Price removed');
                            this.pushChangeToArrayRemovedItem(
                                group,
                                'editedFieldsGenderPriceRemoved',
                                bkpGenderPrice.audienceTypeDescription ? bkpGenderPrice.audienceTypeDescription : 'Per order',
                                bkpGenderPrice
                            );
                        }
                    }
                }

                // Now we fill the "AddedArray"
                group.editedFieldsGenderPriceAdded = [];

                for (let genderPrice of group.productGenderPrices) {
                    if (!genderPrice.id) {
                        let genderPriceAudienceTypeHelperDescription = null;
                        
                        if (genderPrice && genderPrice.audienceTypeHelper && genderPrice.audienceTypeHelper.description) {
                            genderPriceAudienceTypeHelperDescription = genderPrice.audienceTypeHelper.description;
                        } else {
                            genderPriceAudienceTypeHelperDescription = genderPrice.audienceTypeDescription;
                        }

                        this.tagProductToShowApplyAllEvents(group,'Price Added');
                        this.pushChangeToArrayAddedItem(
                            group,
                            'editedFieldsGenderPriceAdded',
                            genderPriceAudienceTypeHelperDescription,
                            genderPrice
                        );
                    }
                }
            },
            findRemovedAndAddedGenderSchedules(group) {
                // Check if group has any backedup schedules
                let groupHasBackedupSchedules = group.backupGroup && group.backupGroup.schedules && group.backupGroup.schedules.length > 0;

                // If it has we will check if any of them were removed (to add to "removedArray")
                if (groupHasBackedupSchedules) {
                    for (let bkpSchedule of group.backupGroup.schedules) {
                        let foundInNormalArray = group.schedules.find(existingSchedule => {return existingSchedule.id == bkpSchedule.id});

                        if (!foundInNormalArray) {
                            this.tagProductToShowApplyAllEvents(group,'Schedule Removed');
                            this.pushChangeToArrayRemovedItem(group, 'editedFieldsSchedulesRemoved', bkpSchedule.startTime, bkpSchedule);
                        }
                    }
                }

                // Now we fill the "AddedArray"
                group.editedFieldsSchedulesAdded = [];

                for (let schedule of group.schedules) {
                    if (!schedule.id) {
                        this.tagProductToShowApplyAllEvents(group,'Schedule Added');
                        this.pushChangeToArrayAddedItem(
                            group,
                            'editedFieldsSchedulesAdded',
                            schedule.startTime,
                            schedule
                        );
                    }
                }
            },
            findRemovedAndAddedGenderResources(group) {
                // Check if group has any backedup resources
                let groupHasBackedupResources = group.backupGroup && group.backupGroup.resources && group.backupGroup.resources.length > 0;

                // If it has we will check if any of them were removed (to add to "removedArray")
                if (groupHasBackedupResources) {
                    for (let bkpResource of group.backupGroup.resources) {
                        let foundInNormalArray = group.resources.find(existingResource => {return existingResource.id == bkpResource.id});

                        if (!foundInNormalArray) {
                            this.tagProductToShowApplyAllEvents(group,'Resource Removed');
                            this.pushChangeToArrayRemovedItem(group, 'editedFieldsResourcesRemoved', bkpResource.name, bkpResource);
                        }
                    }
                }

                // Now we fill the "AddedArray"
                group.editedFieldsResourcesAdded = [];
                for (let resource of group.resources) {
                    let isResourceAdded = false;
                    // there there is any backup
                    if (group.backupGroup.resources.length > 0) {
                        for (let bkpResource of group.backupGroup.resources) {
                            let existsOnBackupGroup = group.backupGroup.resources.find(bkpResource => {return bkpResource.id == resource.id});

                            if (!existsOnBackupGroup) {
                                isResourceAdded = true;
                            }
                        }
                    } else {
                        // else it must be a new resource
                        isResourceAdded = true;
                    }

                    if (isResourceAdded === true) {
                        this.tagProductToShowApplyAllEvents(group,'Resource Added');
                        this.pushChangeToArrayAddedItem(
                            group,
                            'editedFieldsResourcesAdded',
                            resource.name + ' (' + resource.maxSeats + ' Max. Seats)',
                            resource
                        );
                    }
                }
            },
            checkArrayDifference(arrayA, ArrayB) {
                return arrayA.filter(function(x) {
                	// Checking second array does not contain element "x"
                	if (ArrayB.indexOf(x) == -1) {
                		return true;
                    } else {
                		return false;
                    }
                });
            },
            pushChangeToArrayAddedItem(group, destinationArray, niceName, genderPrice) {
                let foundDestinyArray = group[destinationArray];

                if(!foundDestinyArray){
                    this.$set(group, destinationArray, []);
                }

                // Check if item already exists on array
                let foundRepetition;

                if (!foundRepetition) {
                    group[destinationArray].push({
                        genderPriceLabel: genderPrice.genderPriceType === 3 ? null : ((genderPrice.audienceTypeDescription != null ? genderPrice.audienceTypeDescription : '') + (genderPrice.audienceTypeLabel != null ? ' '+ genderPrice.audienceTypeLabel : '')),
                        niceName: niceName,
                        type: 'added',
                        item: genderPrice
                    });
                }
            },
            pushChangeToArrayRemovedItem(group, destinationArray, niceName, genderPrice) {
                // EditedFieldsGenderPriceRemoved
                let foundDestinyArray = group[destinationArray];

                if (!foundDestinyArray) {
                    this.$set(group, destinationArray, []);
                }

                // Check if item already exists on array
                let foundRepetition = group[destinationArray].find(removed => removed.item.id == genderPrice.id);

                if (!foundRepetition) {
                    group[destinationArray].push({
                        genderPriceLabel: genderPrice.genderPriceType === 3 ? null : ((genderPrice.audienceTypeDescription != null ? genderPrice.audienceTypeDescription : '') + (genderPrice.audienceTypeLabel != null ? ' '+ genderPrice.audienceTypeLabel : '')),
                        niceName: niceName,
                        type: 'removed',
                        item: genderPrice
                    });
                }
            },
            groupHasChanges(group) {
                if (!group.backupGroup) return false;

                const ignoredKeys = ['backupGroup', 'isEditing', 'isEdited', 'expanded', 'pricesExpanded', 'resourcesExpanded', 'rulesExpanded', 'invalid','configurations'];
                
                const deepTransform = (obj) => {
                    if (Array.isArray(obj)) {
                        return obj.map(item => deepTransform(item));
                    }
                    
                    if (obj !== null && typeof obj === 'object') {
                        const cleaned = _.omitBy(obj, (value, key) => ignoredKeys.includes(key));
                        return _.mapValues(cleaned, value => deepTransform(value));
                    }
                    
                    return obj;
                };
                
                const groupData = deepTransform(group);
                const backupData = deepTransform(group.backupGroup);
                
                const groupString = JSON.stringify(groupData);
                const backupString = JSON.stringify(backupData);
                
                const hasChanges = groupString !== backupString;
                
                return hasChanges;
            },
            cancelEditGroup(group, index){
                if (group.backupGroup) {
                    const restoreGroup = () => {
                        if (group.id == null) {
                            const groupIndex = this.form.groups.indexOf(group);
                            this.$delete(this.form.groups, groupIndex);
                            this.isEditingPerGroup = false;
                            this.rulePaxNotReady = false;
                            this.setIsReadyToSubmitPricingManager();
                            this.setIsReadyToSubmitResourcesManager(true);
                            return;
                        }

                        let backedupGroup = structuredClone(group.backupGroup);
                        backedupGroup.isEdited = false;
                        backedupGroup.isEditing = false;

                        // Replaces the keys of group object with the ones on the backedupGroup
                        Object.keys(backedupGroup).forEach(key => {
                            this.$set(group, key, backedupGroup[key]);
                        });
                        this.$set(group, 'expanded', false);

                        this.isEditingPerGroup = false;
                        this.rulePaxNotReady = false;
                        this.setIsReadyToSubmitPricingManager();
                        this.setIsReadyToSubmitResourcesManager(!group.resources.some((resource) => resource.maxSeats > resource.availability));
                    };

                    if (!this.groupHasChanges(group)) {
                        restoreGroup();
                        return;
                    }

                    this.$dialog.confirm('\
                        <div class="fs-larg font-weight-600 mb-4">Cancel schedule edit?</div>\
                        <div>Unsaved changes will be lost, continue?</span></div>')
                    .then(restoreGroup)
                    .catch(() => {});
                } else {
                    this.deleteItem(index, 'groups', group);
                }
            },
            getStepState() {
                return { invalid: this.$v.form.invalid, form: this.form };
            },
            readyToSave() {
                const groupsToSave = this.form.groups.filter(group => !this.olderScheduleIds.includes(group.id));
                this.$emit('readyToSave', { invalid: false, form: { ...this.form, groups: groupsToSave }, olderScheduleIds: this.olderScheduleIds });
            },
            buildDatesAndTimes() {
                // Add default dates if we're creating. When editing we already have dates
                // so we don't need to do anything.
                // We must also set the default duration when creating a new product.
                if (!this.getSelectedItem) {
                    // Duration is not a "time" (no AM nor PM)
                    this.defaultDuration = ['01', '00'];

                    if (this.form.schedules === undefined) {
                        this.form.schedules = [];
                    }

                    this.form.schedules.forEach(schedule => {
                        schedule.dateFrom = this.nowDate();
                        schedule.dateTo = this.nowDate();

                        this.setStartTime('09:00', schedule);
                    });

                    this.setDuration(this.defaultDuration[0] + ':' + this.defaultDuration[1]);
                }
            },
            fetchDefaultScheduleStartTime(schedule) {
                if (schedule.startTime !== undefined && schedule.startTime) {
                    // Transform in array and remove seconds

                    let parts = schedule.startTime.split(':');
                    let time = [parts[0], parts[1]];

                    return time;
                }

                return this.defaultScheduleStartTime;
            },
            setDuration(timestamp, schedule) {
                schedule.duration = timestamp + ':00';
            },
            setStartTime(event, schedule) {
                schedule.startTime = event + ':00';
            },
            toggleCapacity() {
                if (this.form.isPrivate) {
                    if (!this.form.capacity || this.form.capacity == null) {
                        this.form.capacity = 0;
                    }
                    this.showCapacity = false;
                } else {
                    this.showCapacity = true;
                }
            },
            removePrincingAgeRow(index) {
                this.form.productGenderPrices.splice(index, 1);
                this.$forceUpdate();
            },
            setAvailabiltyType(availabilityType) {
                if (availabilityType.value !== AvailabilityTypes.LIMITED_BY_RESOURCES) {
                    this.virtualRooster = false;
                }

                this.$set(this.form, 'availabilityType', availabilityType.value);
                this.setAvailabiltyOnGroups(false);
                this.productAcivateUpdateFlag('schedule');
                this.productAcivateUpdateFlag('scheduleAvailability');
                this.validateResources(this.form);
            },
            setAvailabiltyOnGroups(initialConfig) {
                const availabilityType = this.form.availabilityType;

                if (this.form.groups.length > 0) {
                    this.form.groups.map(group => {
                        this.$set(group, 'availabilityType', availabilityType);
                        this.$set(group, 'capacity', this.form.capacity);

                        if (!initialConfig) {
                            // Set group as changed (edited)
                            this.$set(group, 'isEdited', true);
                        }
                    })
                }
            },
            toggleGroupExpanded(group) {
                if (!group.expanded) {
                    this.$set(group, 'expanded', true);
                } else {
                    group.expanded = !group.expanded;
                }
            },
            maxAvailabilityCheck() {
                if (this.form.availabilityType != AvailabilityTypes.FIXED_LIMIT) {
                    return true;
                } else {
                    if (this.form.capacity && this.form.capacity > 0) {
                        return true;
                    } else {
                        return false;
                    }
                }
            },
            // -----------------------------------
            // price editor area
            // -----------------------------------
            toggleExpand(group) {
                if (!group.pricesExpanded) {
                    this.$set(group, 'pricesExpanded', true);
                } else {
                    group.pricesExpanded = !group.pricesExpanded;
                }
            },
            markAllSchedulesEdited() {
                this.form.groups.forEach(group => {
                    group.isEdited = true;
                })
            },
            handleAddGroup() {
                if (this.isReadOnly) return;
                this.addGroup();
            },
            toggleOrderDirection() {
                this.isOrderDesc = !this.isOrderDesc;
            },
            sortGroups(groups) {
                
                const getEarliestStartTime = group => {
                    const times = (group && Array.isArray(group.schedules) ? group.schedules : [])
                        .map(schedule => schedule && schedule.startTime ? schedule.startTime : null)
                        .filter(Boolean)
                        .sort();
                    return times.length ? times[0] : '';
                };
                const base = groups.slice().sort((a, b) => {
                    const startDateA = a.dateFrom || '';
                    const startDateB = b.dateFrom || '';

                    //aqui  dia de inicio
                    const sortDirection = this.isOrderDesc ? -1 : 1;
                    if (startDateA !== startDateB) {
                        return sortDirection * startDateA.localeCompare(startDateB);
                    }

                    //aqui data de termino
                    const endDateA = a.dateTo || '';
                    const endDateB = b.dateTo || '';
                
                    if (endDateA !== endDateB) return sortDirection * endDateA.localeCompare(endDateB);

                    const earliestStartTimeA = getEarliestStartTime(a);
                    const earliestStartTimeB = getEarliestStartTime(b);

                    return sortDirection * earliestStartTimeA.localeCompare(earliestStartTimeB);
                });

                return base;
            },
            formatDateRange(dateFrom, dateTo) {
                // Format as DD/MM/YYYY - DD/MM/YYYY
                if (!dateFrom && !dateTo) return '';
                const format = d => d ? d.split('-').reverse().join('/') : '';

                return `${format(dateFrom)} - ${format(dateTo)}`;
            },
        },
        components: {
            vSelect: extendedVSelect,
            Timepicker,
            Datepicker,
            ResourcesManager,
            MinMaxManager,
            PricingManager,
            WeekDayPicker,
            DurationPicker,
            DatePickerNew,
            ProductScheduleGroupTimeDates,
            RulesManager
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../../assets/scss/_variables.scss';

    .schedule-type-holder {
        &--readonly {
            cursor: not-allowed;
        }

        &__btn {
            line-height: normal;
            height: 80px;
            width: 160px;
            margin-right: 0.5rem;

            &:disabled {
                pointer-events: none;
            }

            &--selected {
                border-color: $color-1;
                border-width: 2px;
            }
        }
    }

    input::-webkit-calendar-picker-indicator,
    input::-webkit-inner-spin-button {
        display: inline-flex;
    }

    input[type=time]::-ms-clear {
        display: inline-flex;
    }

    input[type=time]::-webkit-clear-button {
        display: inline-flex;
    }

    .product-schedule {
        background-color: $lighter;
        border: 1px solid $light;
        
        &__virtual-rooster {
            max-width: 487px;
        }

        &__sort-icon {
            cursor: pointer;
        }

        &__dates-label {
            color: #8ca0b3;
        }

        &__label-input {
            width: 80%;
            max-width: 450px;
        }

        &__dates-input {
            width: 100%;
            max-width: 250px;
        }

        &__panel-holder {
            overflow: visible;

            &--closed {
                max-height: 0;
                overflow: hidden;
            }
        }

        &__warning-dot {
            display: inline-block;
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background: #e74c3c;
            margin-left: 0.5rem;
            vertical-align: middle;
        }

        &__availability-width {
            width: 180px;
        }
    }

    @media (max-width: 1360px) {
        .product-schedule {
            &__availability {
                max-width: 100%;
                width: 100%;
                margin-bottom: 1rem;

                .panel {
                    max-width: 100%;
                    width: 100%;
                }
            }

            &__virtual-rooster {
                max-width: 100%;
                width: 100%;
                margin-bottom: 1rem;
            }
        }
    }
</style>



// WEBPACK FOOTER //
// ProductSchedule.vue?02d02ad6